import { localeOfPrefixedHref } from '@/i18n/locale-prefix'
import type { Locale } from '@/i18n/locales'

import { LOCALE_DEFAULT_CURRENCY, type DisplayCurrency } from './map'
import { CURRENCY_PARAM } from './resolve'

/**
 * The href a link renders once the page's selected display currency is carried onto it —
 * or the href untouched, in every case where carrying it would name no page or a
 * redirecting one.
 *
 * Untouched when: nothing is selected; the destination has no locale (a locale-free
 * surface, so no default to compare against); the destination's locale already defaults
 * to the selected currency (the value would be redundant there, and a redundant `cur`
 * 301s); the href is not a same-origin path (a foreign origin, a protocol-relative URL, a
 * scheme-only address, a bare fragment); or the href already names a currency of its own,
 * which an author chose and this does not second-guess.
 *
 * Only a string href is ever changed. The object form (`{ pathname, query }`) passes
 * through: no caller renders one, and a second code path nothing exercises would be
 * untested by construction.
 */
export function hrefWithDisplayCurrency<T>(
    href: T,
    selected: DisplayCurrency | undefined,
    destinationLocale: Locale | undefined,
): T {
    if (selected === undefined || destinationLocale === undefined) {
        return href
    }
    if (LOCALE_DEFAULT_CURRENCY[destinationLocale] === selected) {
        return href
    }
    if (typeof href !== 'string' || !href.startsWith('/') || href.startsWith('//')) {
        return href
    }

    const hashIndex = href.indexOf('#')
    const hash = hashIndex === -1 ? '' : href.slice(hashIndex)
    const withoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex)
    const queryIndex = withoutHash.indexOf('?')
    const pathname = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
    const params = new URLSearchParams(queryIndex === -1 ? '' : withoutHash.slice(queryIndex))

    if (params.has(CURRENCY_PARAM)) {
        return href
    }
    params.set(CURRENCY_PARAM, selected)

    return `${pathname}?${params.toString()}${hash}` as T
}

/**
 * The same rule for an **already-resolved** address — one whose locale prefix the caller
 * has put on, because nothing downstream will: a destination another Wanderu application
 * serves, or one reached by script rather than by an anchor.
 *
 * The destination locale comes off the address, which is the only thing that knows it
 * here; the navigation layer can consult the active locale instead because it is the one
 * rendering under it. Sharing `localeOfPrefixedHref` with that layer is what stops the two
 * disagreeing about which addresses name a locale.
 */
export function addressWithDisplayCurrency(
    address: string,
    selected: DisplayCurrency | undefined,
): string {
    return hrefWithDisplayCurrency(address, selected, localeOfPrefixedHref(address))
}

/**
 * The same rule for every anchor inside a string of **authored HTML** — editorial copy
 * rendered as markup, whose links reach the page without passing through the navigation
 * layer. Each `href` is treated as an already-resolved address.
 *
 * The attribute value is entity-escaped, so a query separator arrives as `&amp;`: it is
 * unescaped before the address is read and escaped again after, or the existing query
 * would be read as one key named `amp;b`.
 */
export function htmlWithDisplayCurrency(
    html: string,
    selected: DisplayCurrency | undefined,
): string {
    if (selected === undefined) {
        return html
    }

    return html.replace(/href="([^"]*)"/g, (_match, escaped: string) => {
        const address = addressWithDisplayCurrency(escaped.replaceAll('&amp;', '&'), selected)
        return `href="${address.replaceAll('&', '&amp;')}"`
    })
}
